import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
    MdClose,
    MdExitToApp,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdOutlineCalculate,
    MdOutlineLocationOn,
    MdOutlinePrint,
    MdSuperscript
} from 'react-icons/md';
import { useReactToPrint } from 'react-to-print';
import Header from 'components/Header';
import Button from 'components/Button2';
import TextInput from 'components/TextInput2';
import Loader from 'components/Loader';
import axios from 'config/axios';
import { useAuthContext } from 'context/AuthContext';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Reference from 'components/Reference';
import DesmosCalculatorPanel from 'components/DesmosCalculatorPanel';
import './index.css';

import 'katex/dist/katex.min.css';
import renderMathInElement from "katex/dist/contrib/auto-render.js";
import { flattenKatexUnderlines } from 'global/katexWrap';

const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];
const DISABLE_PRINT = ['ReadingTown', 'College Planning Corp', 'A+ Program', 'MT Learning Center', 'PREPARE Test Prep', 'High Peak Education', 'WellEd Labs'];

function AssignmentPreview() {
    const [name, setName] = useState('');
    const [questions, setQuestions] = useState(null);
    const [subject, setSubject] = useState('');
    const [prompt, setPrompt] = useState('');
    const [questionIndex, setQuestionIndex] = useState(0);
    const [directionsOpen, setDirectionsOpen] = useState(false);
    const [navigatorOpen, setNavigatorOpen] = useState(false);
    const [referenceOpen, setReferenceOpen] = useState(false);
    const [isPrint, setIsPrint] = useState(false);
    const [calculatorOpen, setCalculatorOpen] = useState(false);
    const [shortcutsOpen, setShortcutsOpen] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [showAnswer, setShowAnswer] = useState(false);

    const { user } = useAuthContext();
    const printRef = useRef(null);
    const navigate = useNavigate();
    const { assignmentId } = useParams();
    const directionsRef = useOutsideClickHandler(() => setTimeout(() => setDirectionsOpen(false), 10));
    const navigatorRef = useOutsideClickHandler(() => setTimeout(() => setNavigatorOpen(false), 10));

    const prevQuestion = () => {
        if (questionIndex > 0) {
            setQuestionIndex(questionIndex - 1);
        }
    }

    const nextQuestion = () => {
        if (questionIndex < questions.length - 1) {
            setQuestionIndex(questionIndex + 1);
        }
        else {
            // exit the review
            navigate('/assignments');
        }
    }

    const goToQuestion = (index: number) => {
        setQuestionIndex(index);

        let passages = document.getElementById('assignmentPreview__passages');
        let question = document.getElementById('assignmentPreview__questionChoices');

        if (passages) passages.scrollTop = 0;
        if (question) question.scrollTop = 0;
    }

    const renderKatex = () => {
        const katex = document.getElementsByClassName('katex');
        for (let i = 0; i < katex.length; i++) {
            renderMathInElement(katex[i], {
                delimiters: [
                    {left: '$$', right: '$$', display: true},
                    {left: '$', right: '$', display: false},
                    {left: '\\(', right: '\\)', display: false},
                    {left: '\\[', right: '\\]', display: true}
                ],
                trust: true
            });

            katex[i].innerHTML = katex[i].innerHTML.replaceAll('&nbsp;', ' ');
        }

        const wrappingTexts = document.querySelectorAll<HTMLElement>('.katex-html .base');
        for (let i = 0; i < wrappingTexts.length; i++) {
            if (subject === 'Reading and Writing') {
                wrappingTexts[i].classList.add('wrap');
            }
            else {
                wrappingTexts[i].classList.remove('wrap');
            }
        }

        flattenKatexUnderlines(document);

        const math = document.querySelectorAll('.katex-mathml');
        for (let i = 0; i < math.length; i++) {
            math[i].remove();
        }
    }

    const dragMove = (element: HTMLElement, header: HTMLElement) => {
        header.style.cursor = 'grab';
        if (!header) {
            return; 
        }
        let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

        const dragMouseDown = (event) => {
            event.preventDefault();
    
            if (event.type === 'mousedown') {
                pos3 = event.clientX;
                pos4 = event.clientY;
                document.onmousemove = dragElement;
                document.onmouseup = closeDragElement;
            } else if (event.type === 'touchstart') {
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
                document.ontouchmove = dragElement;
                document.ontouchend = closeDragElement;
            }
        }

        const dragElement = (event) => {
            event.preventDefault();

            if (event.type === 'mousemove') {
                pos1 = pos3 - event.clientX;
                pos2 = pos4 - event.clientY;
                pos3 = event.clientX;
                pos4 = event.clientY;
            } else if (event.type === 'touchmove') {
                pos1 = pos3 - event.touches[0].clientX;
                pos2 = pos4 - event.touches[0].clientY;
                pos3 = event.touches[0].clientX;
                pos4 = event.touches[0].clientY;
            }

            if (pos3 > 0 && pos4 > 0 && pos3 < window.innerWidth && pos4 < window.innerHeight) {
                element.style.top = (element.offsetTop - pos2) + 'px';
                element.style.left = (element.offsetLeft - pos1) + 'px';
            }
            else {
                document.onmouseup(event);
                closeDragElement();
            }
        }

        const closeDragElement = () => {
            document.onmouseup = null;
            document.onmousemove = null;
            document.ontouchend = null;
            document.ontouchmove = null;
        }
    
        header.onmousedown = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    
        header.ontouchstart = (event) => {
            if (event.target instanceof Element && (event.target.closest('.digitalReview__popupHeaderIcon') || event.target.closest('.digitalReview__popupHeaderFull'))) {
                return;
            }
            dragMouseDown(event);
        };
    }

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        onAfterPrint: () => setIsPrint(false)
    });

    const onPrint = () =>{
        setIsPrint(true);
    }

    useEffect(() => {
        axios.get(`tutors/assignments/preview/${assignmentId}/`)
            .then(res => {
                // console.log(res.data);
                setName(res.data.name);
                setQuestions(res.data.questions);
                setSubject(res.data.subject);
                setPrompt(res.data.prompt);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        if (questions) {
            renderKatex();
        }
    }, [questions, questionIndex]);

    useEffect(() => {
        if (questions) {
            // dragMove(document.getElementById('calculatorDrag'), document.getElementById('calculatorHeader'));
            // dragMove(document.getElementById('referenceDrag'), document.getElementById('referenceHeader'));
            // dragMove(document.getElementById('shortcutsDrag'), document.getElementById('shortcutsHeader'));
            const explanationDragEl = document.getElementById('explanationDrag');
            const explanationHeaderEl = document.getElementById('explanationHeader');
            if (explanationDragEl && explanationHeaderEl) {
                dragMove(explanationDragEl, explanationHeaderEl);
            }
            
            const referenceDragEl = document.getElementById('referenceDrag');
            const referenceHeaderEl = document.getElementById('referenceHeader');
            if (referenceDragEl && referenceHeaderEl) {
                dragMove(referenceDragEl, referenceHeaderEl);
            }
        
            const shortcutsDragEl = document.getElementById('shortcutsDrag');
            const shortcutsHeaderEl = document.getElementById('shortcutsHeader');
            if (shortcutsDragEl && shortcutsHeaderEl) {
                dragMove(shortcutsDragEl, shortcutsHeaderEl);
            }
        }
    }, [questions]);

    useEffect(() => {
        if (questions) {
            renderKatex();
        }
    }, [directionsOpen]);

    useEffect(() => {
        if (isPrint) {
            renderKatex();
            handlePrint();
        }
    }, [isPrint]);

    if (questions === null) return <Loader />;

    return (
        <>
        <Helmet>
            <title>
                Assignment Preview - Test Center Admin
            </title>
        </Helmet>
        <Header title={name} prevRoutes={[{ name: 'Assignments', path: '/assignments' }]}/>
        <div className='assignmentPreview__box' onCopy={(event) => { event.preventDefault() }}>
            {/* screen that will be appear when click button is working */}
            {isPrint &&
                <div className='assignmentPreview__print' ref={printRef}>
                    <h2 className='assignmentPreview__title'>
                        {name}
                    </h2>
                    {questions.map((question, q) => (
                        <>
                            <div className='assignmentPreview__test'>
                                {/* left passage */}
                                {question.passages.length > 0 && <>
                                    <div className='assignmentPreview__material'>
                                        <div className='assignmentPreview__materialContent'>
                                            <div id='assignmentPreview__passages' className='assignmentPreview__passages'>
                                                {question.figures.map(figure => (
                                                    <img
                                                        className='assignmentPreview__figure'
                                                        key={figure.id}
                                                        src={figure.image}
                                                        alt={(q + 1).toString()}
                                                    />
                                                ))}
                                                {question.passages.map(({ id, passage }) => (
                                                    <div key={id} className='assignmentPreview__passage'>
                                                        <p
                                                            id={id}
                                                            className='katex assignmentPreview__passageText'
                                                        >
                                                            {passage}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='assignmentPreview__verticalLine'></div>
                                </>}
                                {/* right question & answer */}
                                <div className='assignmentPreview__question'>
                                    <div className='assignmentPreview__questionContent'>
                                        {/* header (question num etc) */}
                                        <div className='assignmentPreview__questionHeader'>
                                            {/* question num */}
                                            <p className="assignmentPreview__questionNumber">
                                                {q + 1}
                                            </p>
                                        </div>

                                        <div id='assignmentPreview__questionChoices' className='assignmentPreview__questionChoices'>
                                            {subject === 'Math' &&
                                                question.figures.map(figure => (
                                                    <img
                                                        className='assignmentPreview__figure'
                                                        key={figure.id}
                                                        src={figure.image}
                                                        alt={(q + 1).toString()}
                                                    />
                                                ))
                                            }
                                            {/* question content */}
                                            <p className='assignmentPreview__questionQuestion'>
                                                <span className='katex'>
                                                    {question.question}
                                                </span>
                                            </p>
                                            {question.choices.length
                                            ?  // multiple choice answer
                                            <>
                                                    {question.choices.map((choice, i) => (
                                                        <div key={choice.id} className='assignmentPreview__questionChoiceWrapper'>
                                                            <button
                                                                className={choice.isCorrect
                                                                    ? 'assignmentPreview__questionChoice--correct'
                                                                    : 'assignmentPreview__questionChoice'
                                                                }
                                                                onClick={() => {}}
                                                            >
                                                                <div className='assignmentPreview__questionChoiceOutline'></div>
                                                                <p className='assignmentPreview__questionChoiceLetter'>
                                                                    {MULTIPLE_CHOICES[i]}
                                                                </p>
                                                                {choice.figure
                                                                ?   <img
                                                                        className='assignmentPreview__questionChoiceFigure'
                                                                        key={choice.figure}
                                                                        src={choice.figure}
                                                                        alt={choice.figure}
                                                                    />
                                                                :   <p className='assignmentPreview__questionChoiceText'>
                                                                        <span className='katex'>
                                                                            {choice.choice}
                                                                        </span>
                                                                    </p>
                                                                }
                                                            </button>
                                                        </div>
                                                    ))}
                                                </>
                                            : // input answer
                                            <div className='assignmentPreview__questionAnswerReview'>
                                                    <TextInput
                                                        className='assignmentPreview__questionAnswer'
                                                        inputClassName='assignmentPreview__questionAnswerInput'
                                                        name={name + q}
                                                        value={question.correctAnswers.join(', ') }
                                                    />
                                                </div>
                                            }
                                            <div className='assignmentPreview__marginBottom'></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>Explanation: </div>
                            {question.explanationFigure && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <img
                                        src={question.explanationFigure}
                                        alt="Explanation Figure"
                                        style={{ maxWidth: '70%', height: 'auto' }}
                                    /> 
                                </div> 
                            )}
                            {question.explanation &&
                                <div className='assignmentPreview__printExpl'>
                                    <span className='katex'>{question.explanation}</span>
                                </div>                                
                            }
                        </>
                    ))}
                </div>
            }


            <div className='assignmentPreview'>
                {/* test Header */}
                <div className='assignmentPreview__header'>
                    {/* <div className='assignmentPreview__titleGroup'>
                        <h1 className='assignmentPreview__title'>
                            {name}
                        </h1>
                        <button className='assignmentPreview__directionsButton' onClick={() => setDirectionsOpen(!directionsOpen)}>
                            {directionsOpen
                                ? <u>Directions</u>
                                : <p>Directions</p>
                            }
                            {directionsOpen
                                ? <MdKeyboardArrowUp className='assignmentPreview__directionsIcon' />
                                : <MdKeyboardArrowDown className='assignmentPreview__directionsIcon' />
                            }
                        </button>
                    </div>
                    {directionsOpen &&
                        <div className='assignmentPreview__directions' ref={directionsRef}>
                            <div className='assignmentPreview__directionsBubble'>
                                <p className='katex assignmentPreview__directionsText'>
                                    {prompt
                                        ? prompt
                                        : 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis sollicitudin, mi congue condimentum dignissim, ipsum diam tristique velit, et rutrum orci massa non purus. Etiam vitae ante sed velit blandit condimentum. Donec lectus ipsum, volutpat vel tortor a, blandit elementum felis. Mauris a ultricies quam, at molestie ipsum. Cras nec ante justo. Suspendisse egestas lectus sit amet sem maximus auctor. In ut dignissim lorem, ac tristique eros. Nullam eget sollicitudin quam. Quisque pellentesque cursus justo sed ornare. Integer finibus, tortor at facilisis molestie, ligula augue cursus ipsum, eget auctor enim sem a eros. Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas. Pellentesque suscipit, nunc eget mollis consequat, augue odio suscipit enim, vel varius eros leo sit amet magna. Nunc suscipit leo et metus bibendum, a finibus libero finibus. In lacinia scelerisque urna ut pretium. In at est aliquam, dictum odio non, fringilla tellus. Vivamus commodo molestie urna at dapibus. Nulla facilisi. Maecenas sit amet lorem tincidunt, maximus nunc eu, malesuada velit. Class aptent taciti sociosqu ad litora torquent per conubia nostra, per inceptos himenaeos. Nulla facilisi.'
                                    }
                                </p>
                                <Button
                                    className='assignmentPreview__directionsClose'
                                    onClick={() => setDirectionsOpen(false)}
                                    secondary
                                >
                                    CLOSE
                                </Button>
                            </div>
                        </div>
                    } */}
                    <h1 className='assignmentPreview__title'>
                        {name}
                    </h1>

                    {/* header action */}
                    <div className='assignmentPreview__actionGroup'>
                        {!(user.organizations.some(organization => DISABLE_PRINT.includes(organization))
                            || user.customizations.find(customization => customization === 'Disable Print')
                        ) &&
                            <button
                                className='assignmentPreview__action'
                                onClick={onPrint}
                            >
                                <MdOutlinePrint className='assignmentPreview__actionIcon' />
                                <p className='assignmentPreview__actionText'>Print</p>
                            </button>
                        }
                        {subject === 'Math' && <>
                            <button
                                className={referenceOpen
                                    ? 'assignmentPreview__action--active'
                                    : 'assignmentPreview__action'
                                }
                                onClick={() => {
                                    if (calculatorOpen) setCalculatorOpen(false);
                                    if (shortcutsOpen) setShortcutsOpen(false);
                                    setReferenceOpen(!referenceOpen);
                                }}
                            >
                                <MdSuperscript className='assignmentPreview__actionIcon' />
                                <p className='assignmentPreview__actionText'>Reference</p>
                            </button>
                            <button 
                                className={calculatorOpen
                                    ? 'testReview__action--active'
                                    : 'testReview__action'
                                }
                                onClick={() => {
                                    if (referenceOpen) setReferenceOpen(false);
                                    if (shortcutsOpen) setShortcutsOpen(false);
                                    setCalculatorOpen(!calculatorOpen);
                                }}
                            >
                                <MdOutlineCalculate className='testReview__actionIcon' />
                                <p className='testReview__actionText'>Calculator</p>
                            </button>
                        </>}
                        <button
                            className='assignmentPreview__action'
                            onClick={() => navigate(-1)}
                        >
                            <MdExitToApp className='assignmentPreview__actionIcon' />
                            <p className='assignmentPreview__actionText'>Exit</p>
                        </button>
                    </div>
                    <div id='explanationDrag' className={showExplanation
                        ? 'assignmentPreview__popup--explanation'
                        : 'assignmentPreview__popup--hidden'
                    }>
                        <div id='explanationHeader' className='assignmentPreview__popupHeader'>
                            <h3 className='assignmentPreview__popupHeaderText'>Explanation</h3>
                            <MdClose className='assignmentPreview__popupHeaderIcon' onClick={() => setShowExplanation(false)} />
                        </div>
                        <div className='assignmentPreview__explanationContainer'>
                            {questions[questionIndex].explanationFigure && (
                                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                    <img
                                        src={questions[questionIndex].explanationFigure}
                                        alt="Explanation Figure"
                                        style={{ maxWidth: '80%', height: 'auto' }}
                                    /> 
                                </div> 
                            )}
                            <div className='assignmentPreview__explanationBox katex'>
                                {questions[questionIndex].explanation? questions[questionIndex].explanation: 'No explanation'}
                            </div>
                        </div>
                    </div>
                    <div id='referenceDrag' className={referenceOpen
                        ? 'assignmentPreview__popup--reference'
                        : 'assignmentPreview__popup--hidden'
                    }>
                        <div id='referenceHeader' className='assignmentPreview__popupHeader'>
                            <h3 className='assignmentPreview__popupHeaderText'>Reference</h3>
                            <MdClose className='assignmentPreview__popupHeaderIcon' onClick={() => setReferenceOpen(false)} />
                        </div>
                        <Reference />
                    </div>
                    <DesmosCalculatorPanel
                        open={calculatorOpen}
                        onClose={() => setCalculatorOpen(false)}
                        headerClassName='testReview__popupHeader'
                    />
                </div>
                <div className='assignmentPreview__dottedLine'></div>


                {/* test content */}
                <div className='assignmentPreview__test'>
                    {/* left-passage */}
                    {questions[questionIndex].passages.length > 0 && <>
                        <div className='assignmentPreview__material'>
                            <div className='assignmentPreview__materialContent'>
                                <div id='assignmentPreview__passages' className='assignmentPreview__passages'>
                                    {questions[questionIndex].figures.map(figure => (
                                        <img
                                            className='assignmentPreview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questionIndex + 1).toString()}
                                            draggable={false}
                                        />
                                    ))}
                                    {questions[questionIndex].passages.map(({ id, passage }) => (
                                        <div key={id} className='assignmentPreview__passage'>
                                            <p
                                                id={id}
                                                className='katex assignmentPreview__passageText'
                                            >
                                                {passage}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className='assignmentPreview__verticalLine'></div>
                    </>}
                    
                    {/* right-question */}
                    <div className='assignmentPreview__question'>
                        <div className='assignmentPreview__questionContent'>
                            {/* header */}
                            <div className='assignmentPreview__questionHeader'>
                                <p className="assignmentPreview__questionNumber">
                                    {questionIndex + 1}
                                </p>
                                <div className='assignmentPreview__questionButtons'>
                                    {questions[questionIndex].explanation &&
                                        <button
                                            className={showExplanation
                                                ? 'testPreview__explanation--active'
                                                : 'testPreview__explanation'
                                            }
                                            onClick={() => setShowExplanation(!showExplanation)}
                                        >
                                            Explanation
                                        </button>
                                    }
                                    <button
                                        className={showAnswer
                                            ? 'assignmentPreview__showAnswer--active'
                                            : 'assignmentPreview__showAnswer'
                                        }
                                        onClick={() => setShowAnswer(!showAnswer)}
                                    >
                                        {showAnswer ? 'Hide answer' : 'Show answer'}
                                    </button>
                                </div>
                            </div>

                            <div id='assignmentPreview__questionChoices' className='assignmentPreview__questionChoices'>
                                {subject === 'Math' &&
                                    questions[questionIndex].figures.map(figure => (
                                        <img
                                            className='assignmentPreview__figure'
                                            key={figure.id}
                                            src={figure.image}
                                            alt={(questionIndex + 1).toString()}
                                            draggable={false}
                                        />
                                    ))
                                }
                                {/* question content */}
                                <p className='assignmentPreview__questionQuestion'>
                                    <span className='katex'>
                                        {questions[questionIndex].question}
                                    </span>
                                </p>
                                {questions[questionIndex].choices.length
                                ?   // multiple chioice
                                    <>
                                        {questions[questionIndex].choices.map((choice, i) => (
                                            <div key={choice.id} className='assignmentPreview__questionChoiceWrapper'>
                                                <button 
                                                    className={showAnswer && choice.isCorrect
                                                        ? 'assignmentPreview__questionChoice--correct'
                                                        : 'assignmentPreview__questionChoice'
                                                    }
                                                    onClick={() => {}}
                                                >
                                                    <div className='assignmentPreview__questionChoiceOutline'></div>
                                                    <p className='assignmentPreview__questionChoiceLetter'>
                                                        {MULTIPLE_CHOICES[i]}
                                                    </p>
                                                    {choice.figure
                                                    ?   <img
                                                            className='assignmentPreview__questionChoiceFigure'
                                                            key={choice.figure}
                                                            src={choice.figure}
                                                            alt={choice.figure}
                                                            draggable={false}
                                                        />
                                                    :   <p className='assignmentPreview__questionChoiceText'>
                                                            <span className='katex'>
                                                                {choice.choice}
                                                            </span>
                                                        </p>
                                                    }
                                                </button>
                                            </div>
                                        ))}
                                    </>
                                : // input answer
                                   <div className='assignmentPreview__questionAnswerReview'>
                                        <TextInput
                                            className='assignmentPreview__questionAnswer'
                                            inputClassName='assignmentPreview__questionAnswerInput'
                                            name={name + questionIndex}
                                            value={showAnswer? questions[questionIndex].correctAnswers.toString().replaceAll(',', ', '): ''}
                                        />
                                    </div>
                                }
                                <div className='assignmentPreview__marginBottom'></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='assignmentPreview__dottedLine'></div>
                
                {/* text content bottom */}
                <div className='assignmentPreview__footer'>
                    {questions[questionIndex].category && questions[questionIndex].rootCategory
                    ?   <div className='assignmentPreview__categoryGroup'>
                            <p className='assignmentPreview__rootCategory'>
                                {questions[questionIndex].rootCategory}
                            </p>
                            <p className='assignmentPreview__category'>
                                {questions[questionIndex].category}
                            </p>
                        </div>
                    :   <div></div>
                    }
                    <button
                        className='assignmentPreview__navigatorToggle'
                        onClick={() => setNavigatorOpen(!navigatorOpen)}
                    >
                        Question {questionIndex + 1}/{questions.length}
                        {navigatorOpen
                            ? <MdKeyboardArrowDown className='assignmentPreview__navigatorToggleIcon' />
                            : <MdKeyboardArrowUp className='assignmentPreview__navigatorToggleIcon' />
                        }
                    </button>

                    <div className='assignmentPreview__buttons'>
                        <Button
                            className='assignmentPreview__button'
                            secondary
                            disabled={questionIndex === 0}
                            onClick={prevQuestion}
                        >
                            BACK
                        </Button>
                        <Button
                            className='assignmentPreview__button'
                            onClick={nextQuestion}
                        >
                            NEXT
                        </Button>
                    </div>

                    {/* bottom 'Question' button opened */}
                    {navigatorOpen &&
                        <div className='assignmentPreview__navigator' ref={navigatorRef}>
                            <div className='assignmentPreview__navigatorBubble'>
                                <h2 className='assignmentPreview__navigatorTitle'>
                                    {name} Questions
                                </h2>
                                <div className='assignmentPreview__navigatorLegends'>
                                    <div className='assignmentPreview__navigatorLegend'>
                                        <MdOutlineLocationOn className='assignmentPreview__navigatorLegendCurrent' />
                                        <p className='assignmentPreview__navigatorLegendText'>
                                            Current
                                        </p>
                                    </div>
                                </div>
                                <div className='assignmentPreview__navigatorItems'>
                                    {questions.map((question, i) => (
                                        <button
                                            key={`${question.id}nav`}
                                            className="assignmentPreview__navigatorItem--correct"
                                            onClick={() => {
                                                goToQuestion(i);
                                                setNavigatorOpen(false);
                                            }}
                                        >
                                            {questionIndex === i
                                                ? <u className='assignmentPreview__navigatorItemNumber'>{i + 1}</u>
                                                : <p className='assignmentPreview__navigatorItemNumber'>{i + 1}</p>
                                            }
                                            {questionIndex === i && <MdOutlineLocationOn className='assignmentPreview__navigatorItemCurrent' />}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    }

                </div>
            </div>
        </div>
        </>
    );
}

export default AssignmentPreview;